import type { ShapeDividersCSSVars, MediaContainerWithDividersDesign } from '@wix/thunderbolt-becky-types'
import { CompInfo } from '@wix/thunderbolt-components-native'
import { ParsedSvg } from '@wix/thunderbolt-commons'
import {
	getShapeDividerArgs,
	getShapeDividerImageContent,
	getLayersEffectProp,
	getUnitLengthPercentage,
	CompDesign,
	isMediaContainerWithDividers,
	getCompDesignPerVariant,
} from './ShapeDividers.utils'

const SHAPE_DIVIDER_MOBILE_HEIGHT = '50px'

const getShapeDividerImage = (
	compId: string,
	data: MediaContainerWithDividersDesign['dividerTop'],
	side: string,
	color: string,
	height: string,
	parsedSvg: ParsedSvg
) => {
	const shapeDividerArgs = getShapeDividerArgs({ compId, side, data })
	return getShapeDividerImageContent({ color, height, parsedSvg, ...shapeDividerArgs })
}

const getShapeDividersData = (
	compDesignPerVariantMap: {
		[variantId: string]: CompDesign
	},
	isMobileView: boolean,
	hasResponsiveLayout: boolean
) => {
	/*
	 * Get mobile dividers data - only if we're in mobile view
	 */
	const mobile =
		isMobileView && !hasResponsiveLayout
			? Object.entries(compDesignPerVariantMap).find(([key]) => key !== '')?.[1]
			: undefined

	if (mobile && 'dividerTop' in mobile) {
		const disableMobileTop = mobile.dividerTop?.svgId === 'none'
		const disableMobileBottom = mobile.dividerBottom?.svgId === 'none'
		return {
			topDivider: disableMobileBottom ? null : mobile.dividerTop,
			bottomDivider: disableMobileTop ? null : mobile.dividerBottom,
			noMobileTop: typeof mobile.dividerTop === 'undefined',
			noMobileBottom: typeof mobile.dividerBottom === 'undefined',
			disableMobileTop,
			disableMobileBottom,
		}
	}

	const desktop = compDesignPerVariantMap['']

	return {
		topDivider: isMediaContainerWithDividers(desktop) ? desktop.dividerTop : null,
		bottomDivider: isMediaContainerWithDividers(desktop) ? desktop.dividerBottom : null,
		noMobileTop: true,
		noMobileBottom: true,
		disableMobileTop: false,
		disableMobileBottom: false,
	}
}

const getDividerCSSVars = (
	compId: string,
	divider: MediaContainerWithDividersDesign['dividerTop'] | null,
	isMobileView: boolean,
	noMobile: boolean,
	side: string
) => {
	if (!divider) {
		return {}
	}
	const layersEffect = getLayersEffectProp(divider.layersEffect)
	const height = isMobileView && noMobile ? SHAPE_DIVIDER_MOBILE_HEIGHT : getUnitLengthPercentage(divider.height)
	const offsetX = getUnitLengthPercentage(divider.offsetX)
	const color = divider.color
	const opacity = divider.opacity ?? 1
	const padding = getUnitLengthPercentage(divider.padding)
	const xFlip = divider.flip ? '-1' : '1'
	const flip = `scale(${xFlip}, ${side === 'top' ? '-1' : '1'})`
	const layersPin = layersEffect?.pin

	const dividerCssVars = {
		height,
		offsetX,
		color,
		opacity,
		image: divider
			? getShapeDividerImage(compId, divider, side, color, height, (divider.svgId as any) as ParsedSvg)
			: {},
		padding,
		flip,
		layersEffect,
		layersPin,
	}

	return {
		[`--divider-${side}-image`]: dividerCssVars.image?.image || 'none',
		[`--divider-${side}-size`]: dividerCssVars.image?.size || '',
		[`--divider-${side}-height`]: dividerCssVars.height,
		[`--divider-${side}-offset-x`]: dividerCssVars.offsetX,
		[`--divider-${side}-padding`]: dividerCssVars.padding,
		[`--divider-${side}-color`]: dividerCssVars.color,
		[`--divider-${side}-opacity`]: dividerCssVars.opacity,
		[`--divider-${side}-flip`]: dividerCssVars.flip,
		[`--divider-${side}-layers-size`]: dividerCssVars.layersEffect?.size,
		[`--divider-${side}-layers-y`]: dividerCssVars.layersEffect?.y,
		[`--divider-${side}-layers-x`]: dividerCssVars.layersEffect?.x,
		[`--divider-${side}-layers-pin-factor`]: dividerCssVars.layersPin === 'out' ? 1 : -1,
	}
}

export const getShapeDividersCSSVars = ({
	compId,
	hasResponsiveLayout,
	isMobileView,
	compDesign,
}: Pick<CompInfo, 'hasResponsiveLayout' | 'isMobileView' | 'compId'> & {
	compDesign: CompDesign
}): ShapeDividersCSSVars => {
	const compDesignPerVariantMap = getCompDesignPerVariant(compDesign)
	const { topDivider, bottomDivider, noMobileTop, noMobileBottom } = getShapeDividersData(
		compDesignPerVariantMap,
		isMobileView,
		hasResponsiveLayout
	)

	const topCssVars = getDividerCSSVars(compId, topDivider, isMobileView, noMobileTop, 'top')

	const bottomCssVars = getDividerCSSVars(compId, bottomDivider, isMobileView, noMobileBottom, 'bottom')

	return { ...topCssVars, ...bottomCssVars }
}
